import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import Button from '../components/ui/Button';
import { Eyebrow, SectionHead } from '../components/ui/SectionHead';
import Notice from '../components/ui/Notice';
import DlFacts from '../components/ui/DlFacts';
import { entity } from '../content/en';
import { organizationSchema, webSiteSchema, breadcrumbSchema } from '../components/ui/schema';

const Home = () => {
  const { t, i18n } = useTranslation('cy');

  const schema = [
    organizationSchema(),
    webSiteSchema(),
    breadcrumbSchema([{ label: t('nav.home'), to: '/' }], i18n.language),
  ];

  const pillars = t('home.pillars.items', { returnObjects: true });
  const principles = t('home.stance.principles', { returnObjects: true });
  const stops = t('home.stance.stops.items', { returnObjects: true });
  const roles = t('home.role.groups', { returnObjects: true });
  const connections = t('home.connection.rows', { returnObjects: true });
  const stages = t('home.preview.stages', { returnObjects: true });

  return (
    <Layout>
      <SEO title={t('home.meta.title')} description={t('home.meta.description')} path="/" schema={schema} />

      <section className="cy-hero" id="home-hero">
        <div className="cy-hero__arc" aria-hidden="true" />
        <div className="cy-container">
          <div className="cy-hero__inner">
            <Eyebrow>{t('home.hero.eyebrow')}</Eyebrow>
            <h1 className="cy-hero__title">{t('home.hero.h1')}</h1>
            <p className="cy-kicker">{t('home.hero.kicker')}</p>
            <Notice variant="inline" className="cy-hero__boundary">
              {t('home.hero.boundary')}
            </Notice>
            <div className="cy-hero__cta">
              <Button to="/submit-opportunity">{t('cta.submit')}</Button>
              <Button href="#home-pillars" variant="secondary">{t('cta.explore')}</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="cy-section cy-section--alt" id="home-pillars">
        <div className="cy-container">
          <SectionHead eyebrow={t('home.pillars.eyebrow')} title={t('home.pillars.h2')} lead={t('home.pillars.lead')} />
          <div className="cy-pillar-grid">
            {pillars.map((item) => (
              <Link key={item.to} className="cy-card cy-card--link cy-pillar-card" to={item.to}>
                <p className="cy-pillar-card__ask">{item.question}</p>
                <h3 className="cy-card-title">{item.short}</h3>
                <p className="cy-pillar-card__name">{item.title}</p>
                <p className="cy-card-text">{item.desc}</p>
                <span className="cy-card-arrow">{item.cta}</span>
              </Link>
            ))}
          </div>
          <p className="cy-fineprint cy-section-note">{t('home.pillars.boundary')}</p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="home-selection-principle">
        <div className="cy-container cy-split">
          <div className="cy-split__main">
            <SectionHead eyebrow={t('home.stance.eyebrow')} title={t('home.stance.h2')} lead={t('home.stance.lead')} />
            <p className="cy-section-action">
              <Link className="cy-link" to="/approach#approach-gates">
                {t('home.stance.cta')}
              </Link>
            </p>
          </div>
          <div className="cy-split__aside">
            {principles.map((p) => (
              <div className="cy-principle" key={p.title}>
                <h3 className="cy-principle__title">{p.title}</h3>
                <p className="cy-card-text">{p.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="cy-container cy-stops-wrap">
          <div className="cy-stops">
            <h3 className="cy-stops__title">{t('home.stance.stops.h3')}</h3>
            <p className="cy-stops__lead">{t('home.stance.stops.lead')}</p>
            <ol className="cy-stop-list">
              {stops.map((item, i) => (
                <li className="cy-stop-list__item" key={item.title}>
                  <span className="cy-stop-list__num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h4 className="cy-stops__item-title">{item.title}</h4>
                    <p className="cy-stops__item-body">{item.body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="cy-fineprint cy-section-note">
              {t('home.stance.stops.note')}{' '}
              <Link className="cy-link" to="/property-development#property-not-fit">
                {t('home.stance.stops.notFitLink')}
              </Link>
            </p>
          </div>
        </div>
      </section>

      <section className="cy-section cy-section--alt" id="home-value-role">
        <div className="cy-container">
          <SectionHead title={t('home.role.h2')} lead={t('home.role.lead')} />
          <dl className="cy-role-dl">
            {roles.map((g) => (
              <div className="cy-role-dl__row" key={g.title}>
                <dt>{g.title}</dt>
                <dd>{g.body}</dd>
              </div>
            ))}
          </dl>
          <p className="cy-fineprint cy-section-note">{t('home.role.note')}</p>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-roles">
              {t('home.role.cta')}
            </Link>
          </p>
        </div>
      </section>

      <section className="cy-section" id="home-connection">
        <div className="cy-container">
          <SectionHead eyebrow={t('home.connection.eyebrow')} title={t('home.connection.h2')} lead={t('home.connection.lead')} />
          <ol className="cy-chain">
            {connections.map((r) => (
              <li className="cy-chain__item" key={r.node}>
                <span className="cy-chain__node">{r.node}</span>
                <div className="cy-chain__body">
                  <h3 className="cy-chain__pair">{r.pair}</h3>
                  <p className="cy-card-text">{r.link}</p>
                </div>
              </li>
            ))}
          </ol>
          <Notice variant="compliance" className="cy-notice--inline cy-section-note">
            {t('home.connection.boundary')}
          </Notice>
          <p className="cy-section-action">
            <Link className="cy-link" to="/approach#approach-by-pillar">
              {t('home.connection.cta')}
            </Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt" id="home-approach-preview">
        <div className="cy-container">
          <SectionHead eyebrow={t('home.preview.eyebrow')} title={t('home.preview.h2')} />
          <ol className="cy-stage-flow">
            {stages.map((name, i) => (
              <li key={name} className="cy-stage-flow__item">
                <span className="cy-stage__num">{String(i + 1).padStart(2, '0')}</span>
                <span className="cy-stage-flow__name">{name}</span>
              </li>
            ))}
          </ol>
          <p className="cy-fineprint cy-section-note">{t('home.preview.note')}</p>
          <p className="cy-section-action cy-section-action--row">
            <Link className="cy-link" to="/approach#approach-gates">{t('home.preview.lensGate')}</Link>
            <span aria-hidden="true">·</span>
            <Link className="cy-link" to="/property-development#property-assessment">{t('home.preview.lensProperty')}</Link>
            <span aria-hidden="true">·</span>
            <Link className="cy-link" to="/approach#approach-stages">{t('home.preview.cta')}</Link>
          </p>
        </div>
      </section>

      <section className="cy-section" id="home-local-proof">
        <div className="cy-container cy-container--reading">
          <SectionHead eyebrow={t('home.local.eyebrow')} title={t('home.local.h2')} />
          <DlFacts
            rows={[
              { label: t('ui.labels.entity'), value: entity.name },
              { label: t('ui.labels.address'), value: entity.address },
              { label: t('ui.labels.phone'), value: entity.phone, href: entity.phoneHref },
              { label: t('ui.labels.email'), value: entity.email, href: entity.emailHref },
            ]}
          />
          <p className="cy-fineprint cy-section-note">
            {t('home.local.boundary')}{' '}
            <Link className="cy-link" to="/about#about-entity">
              {t('home.local.aboutLink')}
            </Link>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--cta" id="home-final-cta">
        <div className="cy-container cy-cta-band">
          <h2 className="cy-title cy-cta-band__title">{t('home.finalCta.h2')}</h2>
          <p className="cy-lede cy-cta-band__lede">{t('home.finalCta.body')}</p>
          <div className="cy-cta-band__actions">
            <Button to="/submit-opportunity">{t('cta.submit')}</Button>
            <Button to="/approach" variant="secondary">{t('home.finalCta.review')}</Button>
          </div>
          <p className="cy-cta-band__contact">
            <Link className="cy-link" to="/contact">{t('home.finalCta.contact')}</Link>
          </p>
          <p className="cy-fineprint cy-cta-band__privacy">
            {t('home.finalCta.privacy')}{' '}
            <Link className="cy-link" to="/privacy">{t('ui.privacyPolicy')}</Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
